import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useRBAC } from "../hooks/useRBAC";
import { NavIcons } from "./ui/Icons";
import { getTabsForRole } from "../config/mobileNavConfig";
import { ALL_PAGES } from "../config/roleConfig";
import { isOwner, isOwnerOnlyPath } from "../config/sidebarItems";
import MobileNavSheet from "./MobileNavSheet";
import type { UserRole } from "../types/core";

// =============================================================================
// MOBILE BOTTOM NAV — horizontally scrollable all-pages tab bar
// =============================================================================
// PURPOSE: Fixed bottom bar with EVERY accessible page as a swipeable tab.
// Replaces sidebar as the primary navigation on mobile (< 1024px). A slim
// grab-handle strip above the tabs (or a swipe-up gesture on the bar) opens
// MobileNavSheet, a 3-column grid of every accessible page.
//
// ARCHITECTURE:
// - Item list comes from config/mobileNavConfig.ts (single source of truth)
// - Role filtering uses canAccessPage() from roleConfig.ts
// - Icons imported from components/ui/Icons.tsx (shared with Sidebar)
// - Hidden on desktop via lg:hidden
// =============================================================================

// Edge fade on the scroller only (never the glass nav — that would fade the border)
const SCROLLER_EDGE_FADE =
  "linear-gradient(to right, transparent 0, black 16px, black calc(100% - 16px), transparent 100%)";

const SWIPE_UP_MIN_DY = -24;

const MobileBottomNav: React.FC = () => {
  const { user, realUser } = useAuth();
  const { role } = useRBAC();
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const swipeFiredRef = useRef(false);

  const effectiveRole = (role as UserRole) || "viewer";

  // Compute the single ordered item list for this role
  // Owner-only pages (ownerOnly in sidebarItems) drop out unless realUser is Mako,
  // same rule as the Sidebar + RouteGuard.
  const owner = isOwner(realUser?.email);
  const { allItems } = useMemo(
    () =>
      getTabsForRole(
        effectiveRole,
        ALL_PAGES.filter((p) => owner || !isOwnerOnlyPath(p)),
        user?.id ?? null
      ),
    [effectiveRole, owner, user?.id]
  );

  // Auto-center the active tab in the scroller when the route changes
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const activeItem = allItems.find(
      (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
    );
    if (!activeItem) return;
    const el = scroller.querySelector<HTMLElement>(
      `[data-nav-path="${CSS.escape(activeItem.path)}"]`
    );
    if (!el) return;
    scroller.scrollTo({
      left: el.offsetLeft - (scroller.clientWidth - el.offsetWidth) / 2,
      behavior: "smooth",
    });
  }, [location.pathname, allItems]);

  // Don't render if user is not authenticated or there's nothing to navigate to
  if (!user || allItems.length <= 1) return null;

  // Resolve icon component from iconKey string
  const getIcon = (iconKey: string, className: string) => {
    const IconComponent = NavIcons[iconKey as keyof typeof NavIcons];
    if (!IconComponent) return null;
    return <IconComponent className={className} />;
  };

  // Swipe-up on the whole nav opens the sheet — guarded to fire once per gesture.
  // touchAction: "pan-x" on the scroller keeps native horizontal scroll working
  // while letting this vertical-swipe handler still see touchmove events.
  const handleTouchStart = (e: React.TouchEvent<HTMLElement>) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    swipeFiredRef.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLElement>) => {
    if (!touchStartRef.current || swipeFiredRef.current) return;
    const t = e.touches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    if (dy < SWIPE_UP_MIN_DY && Math.abs(dy) > Math.abs(dx) * 1.5) {
      swipeFiredRef.current = true;
      setSheetOpen(true);
    }
  };

  const handleTouchEnd = () => {
    touchStartRef.current = null;
    swipeFiredRef.current = false;
  };

  return (
    <>
      <nav
        data-testid="mobile-nav-bar"
        className="liquid-glass border-t border-black/[0.08] z-40 lg:hidden"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Grab-handle strip — tap or swipe up to open the all-pages sheet */}
        <button
          onClick={() => setSheetOpen(true)}
          className="w-full flex items-center justify-center pt-1.5 pb-0.5"
          aria-label="All pages"
          data-testid="mobile-nav-handle"
        >
          <span className="w-8 h-1 rounded-full bg-black/[0.15]" />
        </button>

        {/* Horizontally scrollable tab row */}
        <div
          ref={scrollerRef}
          data-testid="mobile-nav-scroller"
          className="flex items-stretch overflow-x-auto scrollbar-none"
          style={{
            touchAction: "pan-x",
            WebkitMaskImage: SCROLLER_EDGE_FADE,
            maskImage: SCROLLER_EDGE_FADE,
          }}
        >
          {allItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-nav-path={item.path}
              className={({ isActive }) =>
                `flex-none w-[72px] flex flex-col items-center justify-center py-2 gap-0.5 min-h-[56px] transition-colors active:bg-black/[0.06] ${
                  isActive ? "text-blue-400" : "text-glass-muted"
                }`
              }
            >
              {getIcon(item.iconKey, "w-6 h-6")}
              <span className="token-10 font-medium leading-tight truncate w-full text-center px-1">
                {item.label}
              </span>
            </NavLink>
          ))}
        </div>
      </nav>

      <MobileNavSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} items={allItems} />
    </>
  );
};

export default MobileBottomNav;
